import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getKingsChatLoginUrl, roleHomePath } from "../lib/config";
import {
  saveIntendedRedirect,
  lockPostAuthDestination,
  getIntendedRedirect,
  sanitizeIntendedPath,
} from "../lib/intendedRedirect";
import { BrandLogo } from "../components/BrandLogo";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../utils/ThemeProvider";
import { cn } from "../lib/utils";

function GoldWings({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 120 20" fill="none" aria-hidden="true">
      <path d="M2 10h28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 5h22" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
      <path d="M14 15h16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
      <path d="M90 10h28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M90 5h22" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
      <path d="M90 15h16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
}

function GoldStars({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-2 ${className}`} aria-hidden="true">
      {[0.55, 0.75, 1, 0.75, 0.55].map((opacity, i) => (
        <span
          key={i}
          className="text-[10px] leading-none"
          style={{ color: `rgba(201, 162, 39, ${opacity})` }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function goHome(navigate, user, redirectedRef) {
  if (redirectedRef?.current) return;
  if (redirectedRef) redirectedRef.current = true;

  const home = lockPostAuthDestination(
    user?.home_path || roleHomePath(user?.role) || "/"
  );
  navigate(home, { replace: true });
}

function isAdminRedirect(redirect) {
  const path = (sanitizeIntendedPath(redirect) || "").split("?")[0] || "";
  return path === "/admin" || path.startsWith("/admin/");
}

/**
 * @param {{ mode?: "member" | "admin" }} props
 * member — email/phone, with optional KingsChat
 * admin  — KingsChat only for staff
 */
export function Login({ mode: modeProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { requestLogin, verifyLoginOtp, resendLoginOtp, loginWithToken } = useAuth();
  const { isDark } = useTheme();

  const mode = useMemo(() => {
    if (modeProp === "admin" || modeProp === "member") return modeProp;
    if (location.pathname.startsWith("/portal") || location.pathname.startsWith("/admin/login")) {
      return "admin";
    }
    const redirect =
      searchParams.get("redirect") || getIntendedRedirect() || "";
    if (isAdminRedirect(redirect)) return "admin";
    return "member";
  }, [modeProp, location.pathname, searchParams]);

  const isAdminLogin = mode === "admin";

  const [step, setStep] = useState("email"); // email | otp
  const [identifier, setIdentifier] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [kcLoading, setKcLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const handledToken = useRef(false);
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    const token = searchParams.get("token");
    const errorParam = searchParams.get("error");
    const messageParam = searchParams.get("message");
    const redirect = searchParams.get("redirect");

    // Only persist meaningful destinations — do not overwrite with "/" from OAuth return.
    if (redirect) saveIntendedRedirect(redirect);

    if (errorParam) {
      setError(decodeURIComponent(messageParam || "KingsChat authentication failed"));
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    if (token && !handledToken.current) {
      handledToken.current = true;
      setKcLoading(true);
      loginWithToken(token)
        .then((userData) => {
          window.history.replaceState({}, document.title, location.pathname);
          goHome(navigate, userData, redirectedRef);
        })
        .catch((err) => {
          setError(err.message || "KingsChat login failed");
          window.history.replaceState({}, document.title, location.pathname);
        })
        .finally(() => setKcLoading(false));
    }
  }, [searchParams, loginWithToken, navigate, location.pathname]);

  const handleKingsChatLogin = () => {
    const redirect =
      sanitizeIntendedPath(searchParams.get("redirect")) ||
      getIntendedRedirect() ||
      (isAdminLogin ? "/admin" : null);
    if (redirect) saveIntendedRedirect(redirect);
    window.location.href = getKingsChatLoginUrl(redirect || undefined);
  };

  const handleIdentifierSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const result = await requestLogin(identifier.trim());
      if (result.requiresOtp) {
        setStep("otp");
        setOtp("");
        setMaskedEmail(result.maskedEmail || result.masked_email || "");
        setInfo(result.message || "We sent a verification code to your email");
        setResendIn(result.resendAfter || result.resend_after || 60);
      } else {
        goHome(navigate, result.user, redirectedRef);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Could not sign in with this email or phone"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const result = await verifyLoginOtp(identifier.trim(), otp.trim());
      goHome(navigate, result.data?.user || result.user, redirectedRef);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    setResending(true);
    try {
      const result = await resendLoginOtp(identifier.trim());
      setInfo(result.message || "A new verification code was sent");
      setResendIn(result.resendAfter || result.resend_after || 60);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not resend code");
    } finally {
      setResending(false);
    }
  };

  const inputClass = (field) =>
    cn(
      "w-full rounded-2xl border px-4 py-3.5 text-base outline-none transition",
      focusedField === field
        ? "border-[var(--login-gold)] ring-2 ring-[var(--login-gold)]/25"
        : isDark
          ? "border-white/20 bg-white/5 text-white placeholder:text-white/35"
          : "bg-input-background text-foreground placeholder:text-muted-foreground",
      !isDark && focusedField !== field && "border-border"
    );

  if (kcLoading) {
    return (
      <div
        className={cn(
          "login-shell min-h-screen flex items-center justify-center",
          isDark ? "bg-[var(--login-navy-deep)]" : "bg-background"
        )}
      >
        <div className="text-center login-fade-up">
          <BrandLogo imgClassName="h-16 w-16 mx-auto mb-5 ring-2 ring-[var(--login-gold)]/40" />
          <Loader2 className="animate-spin mx-auto text-[var(--login-gold)]" size={28} />
          <p className={cn("mt-4 text-sm", isDark ? "text-white/70" : "text-muted-foreground")}>
            Signing in with KingsChat…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "login-shell relative min-h-[100svh] overflow-x-hidden overflow-y-auto",
        isDark ? "bg-[var(--login-navy-deep)] text-white" : "bg-background text-foreground"
      )}
    >
      {isDark ? (
        <>
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(11,30,122,0.95), transparent 55%), linear-gradient(160deg, #0b1e7a 0%, #060f42 55%, #040a2e 100%)",
            }}
          />
          <div className="login-facet absolute inset-0 opacity-60" />
          <div className="login-glow-orb absolute -top-24 -left-16 h-80 w-80 rounded-full bg-[var(--login-gold)]/20 blur-3xl" />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(135deg, #bfdbfe 0%, #c7d2fe 50%, #ddd6fe 100%)",
            }}
          />
          <div className="login-facet login-facet-light absolute inset-0 opacity-70" />
          <div className="login-glow-orb absolute -top-24 -left-16 h-80 w-80 rounded-full bg-[var(--login-gold)]/25 blur-3xl" />
        </>
      )}

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col px-4 py-5 sm:px-6 sm:py-6 lg:px-8 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1.25rem,env(safe-area-inset-top))]">
        <div className="flex items-center justify-between gap-2">
          <Link
            to="/"
            className={cn(
              "inline-flex min-h-11 items-center gap-2 text-sm transition",
              isDark ? "text-white/70 hover:text-[var(--login-gold-soft)]" : "text-muted-foreground hover:text-primary"
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span className="truncate">Back to site</span>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={cn(
                "hidden sm:inline text-[10px] uppercase tracking-[0.35em]",
                isDark ? "text-[var(--login-gold-soft)]/80" : "text-primary/70"
              )}
            >
              {isAdminLogin ? "Staff Portal" : "Member Portal"}
            </span>
            <ThemeToggle variant={isDark ? "brand" : "panel"} className="min-h-11 min-w-11 h-11 w-11" />
          </div>
        </div>

        <div className="flex flex-1 flex-col items-center justify-start sm:justify-center gap-6 sm:gap-10 py-6 sm:py-10 lg:flex-row lg:items-center lg:justify-between lg:gap-16">
          <div className="w-full max-w-md text-center lg:text-left login-fade-up">
            <BrandLogo
              className="login-mark-in inline-flex lg:block"
              imgClassName={cn(
                "h-16 w-16 sm:h-20 sm:w-20",
                isDark && "ring-2 ring-[var(--login-gold)]/35"
              )}
            />
            <div className="mt-5 flex justify-center lg:justify-start">
              <GoldWings className={cn("h-5 w-28", isDark ? "text-[var(--login-gold)]" : "text-[#c9a227]")} />
            </div>
            <h1
              className={cn(
                "mt-4 font-display text-4xl sm:text-5xl tracking-tight",
                isDark ? "text-white" : "text-foreground"
              )}
            >
              CELSZC
            </h1>
            <p
              className={cn(
                "mt-2 text-xs uppercase tracking-[0.28em]",
                isDark ? "text-white/70" : "text-muted-foreground"
              )}
            >
              Christ Embassy Lagos Sub Zone C
            </p>
            <GoldStars className="mt-4 lg:justify-start" />
            {isAdminLogin && (
              <p
                className={cn(
                  "mt-4 hidden text-base leading-relaxed lg:block max-w-sm",
                  isDark ? "text-white/70" : "text-muted-foreground"
                )}
              >
                Staff sign in with KingsChat to manage churches, follow-up, reports, and zone operations.
              </p>
            )}
          </div>

          <div className="w-full max-w-md login-fade-up-delay min-w-0">
            <div
              className={cn(
                "relative overflow-hidden rounded-2xl sm:rounded-3xl p-5 sm:p-7 lg:p-8",
                isDark
                  ? "border border-white/15 bg-white/[0.07] shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl"
                  : "border border-border bg-card shadow-lg"
              )}
            >
              <p
                className={cn(
                  "text-[11px] uppercase tracking-[0.3em]",
                  isDark ? "text-[var(--login-gold-soft)]" : "text-primary"
                )}
              >
                {isAdminLogin ? "Staff access" : "Welcome back"}
              </p>
              <h2
                className={cn(
                  "mt-2 text-2xl font-semibold tracking-tight",
                  isDark ? "text-white" : "text-foreground"
                )}
              >
                {isAdminLogin
                  ? "Sign in with KingsChat"
                  : step === "otp"
                    ? "Enter verification code"
                    : "Member sign in"}
              </h2>
              <p className={cn("mt-1.5 text-sm", isDark ? "text-white/55" : "text-muted-foreground")}>
                {isAdminLogin
                  ? "Sign in with KingsChat for staff tools. Use member login (email/phone) for the member portal."
                  : step === "otp"
                    ? `We sent a 6-digit code to ${maskedEmail || "your email"}`
                    : "Sign in with email, phone, or KingsChat"}
              </p>

              {error && (
                <div
                  className={cn(
                    "mt-5 rounded-xl px-4 py-3 text-sm border",
                    isDark
                      ? "border-red-400/30 bg-red-500/15 text-red-100"
                      : "border-red-200 bg-red-50 text-red-700"
                  )}
                >
                  {error}
                </div>
              )}

              {info && !error && (
                <div
                  className={cn(
                    "mt-5 rounded-xl px-4 py-3 text-sm border",
                    isDark
                      ? "border-[var(--login-gold)]/30 bg-[var(--login-gold)]/10 text-[var(--login-gold-soft)]"
                      : "border-primary/20 bg-primary/5 text-primary"
                  )}
                >
                  {info}
                </div>
              )}

              {isAdminLogin ? (
                <div className="mt-6 space-y-4">
                  <button
                    type="button"
                    onClick={handleKingsChatLogin}
                    className={cn(
                      "flex w-full min-h-12 items-center justify-center gap-2.5 rounded-2xl px-4 py-3.5 text-base font-semibold transition",
                      isDark
                        ? "border border-white/20 bg-white/10 text-white hover:bg-white/15"
                        : "bg-primary text-primary-foreground hover:brightness-110"
                    )}
                  >
                    <img
                      src="https://kingsch.at/h/css/images/favicon.ico"
                      alt=""
                      width={20}
                      height={20}
                      className="h-5 w-5 rounded-sm"
                    />
                    Continue with KingsChat
                  </button>
                  <p className={cn("text-center text-xs", isDark ? "text-white/45" : "text-muted-foreground")}>
                    Use KingsChat for the staff portal. For live services and member features,{" "}
                    <Link
                      to="/login"
                      className={cn(
                        "font-medium underline-offset-2 hover:underline",
                        isDark ? "text-[var(--login-gold-soft)]" : "text-primary"
                      )}
                    >
                      sign in as a member
                    </Link>
                    .
                  </p>
                </div>
              ) : step === "email" ? (
                <form onSubmit={handleIdentifierSubmit} className="mt-6 space-y-4">
                  <div>
                    <label
                      className={cn(
                        "mb-1.5 block text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-white/60" : "text-muted-foreground"
                      )}
                    >
                      Email or phone
                    </label>
                    <input
                      type="text"
                      required
                      autoComplete="username"
                      autoFocus
                      inputMode="email"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      onFocus={() => setFocusedField("identifier")}
                      onBlur={() => setFocusedField(null)}
                      className={inputClass("identifier")}
                      placeholder="you@celszc.org or 0803…"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      "flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold transition disabled:opacity-60",
                      isDark
                        ? "border border-white/20 bg-white/10 text-white hover:bg-white/15"
                        : "bg-primary text-primary-foreground hover:brightness-110"
                    )}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Continue…
                      </>
                    ) : (
                      "Continue"
                    )}
                  </button>

                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className={cn("w-full border-t", isDark ? "border-white/15" : "border-border")} />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase tracking-wider">
                      <span className={cn("px-3", isDark ? "bg-[#0b1648] text-white/45" : "bg-card text-muted-foreground")}>
                        or
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleKingsChatLogin}
                    className={cn(
                      "flex w-full min-h-12 items-center justify-center gap-2.5 rounded-2xl px-4 py-3.5 text-sm font-semibold transition",
                      isDark
                        ? "border border-white/20 bg-transparent text-white hover:bg-white/10"
                        : "border border-input bg-background text-foreground hover:bg-muted"
                    )}
                  >
                    <img
                      src="https://kingsch.at/h/css/images/favicon.ico"
                      alt=""
                      width={20}
                      height={20}
                      className="h-5 w-5 rounded-sm"
                    />
                    Continue with KingsChat
                  </button>
                </form>
              ) : (
                <form onSubmit={handleOtpSubmit} className="mt-6 space-y-4">
                  <div>
                    <label
                      className={cn(
                        "mb-1.5 block text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-white/60" : "text-muted-foreground"
                      )}
                    >
                      Verification code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      autoFocus
                      maxLength={6}
                      pattern="[0-9]{6}"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onFocus={() => setFocusedField("otp")}
                      onBlur={() => setFocusedField(null)}
                      className={cn(inputClass("otp"), "tracking-[0.35em] text-center font-semibold")}
                      placeholder="000000"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className={cn(
                      "flex w-full min-h-12 items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold transition disabled:opacity-60",
                      isDark
                        ? "border border-white/20 bg-white/10 text-white hover:bg-white/15"
                        : "bg-primary text-primary-foreground hover:brightness-110"
                    )}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Verifying…
                      </>
                    ) : (
                      "Verify & sign in"
                    )}
                  </button>

                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setStep("email");
                        setOtp("");
                        setError("");
                        setInfo("");
                        setMaskedEmail("");
                      }}
                      className={cn(
                        "min-h-11 px-2",
                        isDark ? "text-white/60 hover:text-white" : "text-muted-foreground hover:text-primary"
                      )}
                    >
                      Use a different email or phone
                    </button>
                    <button
                      type="button"
                      disabled={resendIn > 0 || resending}
                      onClick={handleResend}
                      className={cn(
                        "min-h-11 px-2 font-medium disabled:opacity-50",
                        isDark ? "text-[var(--login-gold-soft)]" : "text-primary"
                      )}
                    >
                      {resending
                        ? "Sending…"
                        : resendIn > 0
                          ? `Resend in ${resendIn}s`
                          : "Resend code"}
                    </button>
                  </div>
                </form>
              )}
            </div>
            {isAdminLogin && (
              <p className={cn("mt-6 text-center text-xs", isDark ? "text-white/40" : "text-muted-foreground")}>
                Need access? Contact your zonal administrator.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminLogin() {
  return <Login mode="admin" />;
}

export function MemberLogin() {
  return <Login mode="member" />;
}
